/**
 * Ocean Wave Loop - A relaxing ocean wave animation with play/pause controls.
 */

import { StrictMode, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

function App() {
  const [isPlaying, setIsPlaying] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let offset = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    const draw = () => {
      if (!isPlaying) {
        animationFrameId = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background Gradient
      const bgGrad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bgGrad.addColorStop(0, "#00b4db");
      bgGrad.addColorStop(1, "#0083b0");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw 3 waves
      const waves = [
        {
          amplitude: 50,
          frequency: 0.01,
          speed: 0.02,
          color: "rgba(255, 255, 255, 0.3)",
          yOffset: canvas.height * 0.6,
        },
        {
          amplitude: 70,
          frequency: 0.008,
          speed: 0.015,
          color: "rgba(255, 255, 255, 0.2)",
          yOffset: canvas.height * 0.7,
        },
        {
          amplitude: 60,
          frequency: 0.012,
          speed: 0.01,
          color: "rgba(255, 255, 255, 0.4)",
          yOffset: canvas.height * 0.8,
        },
      ];

      waves.forEach((wave) => {
        ctx.beginPath();
        ctx.fillStyle = wave.color;
        ctx.moveTo(0, canvas.height);

        for (let x = 0; x <= canvas.width; x++) {
          const y =
            wave.yOffset +
            Math.sin(x * wave.frequency + offset * wave.speed * 100) * wave.amplitude;
          ctx.lineTo(x, y);
        }

        ctx.lineTo(canvas.width, canvas.height);
        ctx.fill();
      });

      offset += 1;
      animationFrameId = requestAnimationFrame(draw);
    };

    animationFrameId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isPlaying]);

  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#0083b0",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: "block",
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: "40px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          textAlign: "center",
          color: "white",
          fontFamily: "system-ui, sans-serif",
          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
        }}
      >
        <h1 style={{ margin: "0 0 20px 0", fontSize: "2rem", opacity: 0.9 }}>Ocean Wave Loop</h1>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          style={{
            padding: "12px 24px",
            fontSize: "1.1rem",
            cursor: "pointer",
            backgroundColor: "rgba(255,255,255,0.2)",
            color: "white",
            border: "2px solid white",
            borderRadius: "30px",
            backdropFilter: "blur(5px)",
            transition: "all 0.2s",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)";
          }}
        >
          {isPlaying ? "Pause Waves" : "Play Waves"}
        </button>
      </div>
    </div>
  );
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Missing #root element");
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
